/**
 * Midnight special hooks: overnight-shift ordering + admin configuration.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiConfig } from "../config/msalConfig";

export interface MidnightOrderingStatus {
  enabled: boolean;
  is_open: boolean;
  order_date: string | null;
  target_date: string | null;
  min_date: string;
  max_date: string;
  cutoff_time: string;
  max_advance_days: number;
  allow_salad: boolean;
  offer_special: boolean;
  current_time: string;
  message: string;
}

export interface MidnightMenu {
  ordering_status: MidnightOrderingStatus;
  special: {
    id: number;
    date: string;
    name: string;
    description: string | null;
  } | null;
}

export interface MidnightSettings {
  enabled: boolean;
  order_cutoff: string;
  allow_salad: boolean;
  offer_special: boolean;
  max_advance_days: number;
  print_enabled: boolean;
  print_time: string;
  printer_name: string | null;
  last_print_date: string | null;
  last_print_at: string | null;
  last_print_status: "success" | "failed" | null;
  last_print_detail: string | null;
}

export interface UpdateMidnightSettingsRequest {
  enabled?: boolean;
  order_cutoff?: string;
  allow_salad?: boolean;
  offer_special?: boolean;
  max_advance_days?: number;
  print_enabled?: boolean;
  print_time?: string;
  printer_name?: string;
}

export function useMidnightMenu() {
  return useQuery<MidnightMenu>({
    queryKey: ["midnight-menu"],
    queryFn: async () => {
      const response = await fetch(`${apiConfig.baseUrl}/menu/midnight`);
      if (!response.ok) {
        throw new Error("Failed to fetch midnight menu");
      }
      return response.json();
    },
    refetchInterval: 60_000, // window opens/closes on the clock
  });
}

export function useMidnightSettings() {
  return useQuery<MidnightSettings>({
    queryKey: ["midnight-settings"],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/midnight/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch midnight settings");
      }
      return response.json();
    },
  });
}

export function useUpdateMidnightSettings() {
  const queryClient = useQueryClient();

  return useMutation<MidnightSettings, Error, UpdateMidnightSettingsRequest>({
    mutationFn: async (data) => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/midnight/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update midnight settings");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["midnight-settings"] });
      queryClient.invalidateQueries({ queryKey: ["midnight-menu"] });
    },
  });
}

export function useMidnightTestPrint() {
  const queryClient = useQueryClient();

  return useMutation<{ status: string; job_id: number; printer: string }, Error, void>({
    mutationFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/midnight/test-print`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Test print failed");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["midnight-settings"] });
    },
  });
}
